import React, { useEffect } from "react";
import PapaParse from "papaparse";

import { Upload } from "antd";
import Icon from "@ant-design/icons";

import "./csv-fileupload.scss";
import * as XLSX from "xlsx";

const { Dragger } = Upload;

interface FileInfo {
  name: string;
  size: number;
  type: string;
}

interface CSVFileUploadProps {
  name: string;
  text: string;
  hint: string | HTMLElement;
  encoding?: string;
  onFileLoaded: (data: Array<any>, fileInfo: FileInfo) => any;
  onError?: (error: Error) => void;
}

// https://learn.microsoft.com/en-us/previous-versions/office/office-2007-resource-kit/ee309278%28v=office.12%29
const typeXLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const CSVFileUpload: React.FC<CSVFileUploadProps> = ({
  name,
  text,
  hint,
  encoding = "UTF-8",
  onFileLoaded,
  onError,
  ...props
}) => {
  const parserOptions = {
    header: true,
    dynamicTyping: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().replace(/\W/g, "_"),
  };

  const handleFileChange = (file: any) => {
    let reader: FileReader = new FileReader();
    const fileInfo: FileInfo = {
      name: file.name,
      size: file.size,
      type: file.type,
    };

    reader.onload = (e) => {
      let formattedData: any;
      let csvData: any;

      if (file.type === typeXLSX) {
        const workbook = XLSX.read(e?.target?.result, {
          type: "binary",
        });

        formattedData = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
      } else {
        formattedData = reader.result;
      }

      csvData = PapaParse.parse(
        formattedData as string,
        Object.assign(parserOptions, {
          error: onError,
          encoding: encoding,
        }),
      );

      onFileLoaded(csvData?.data ?? [], fileInfo);
    };

    // binary for XLSX due to zipped format
    if (file.type === typeXLSX) reader.readAsBinaryString(file);
    else reader.readAsText(file, encoding);

    return false;
  };

  return (
    <div className="csv-fileupload">
      <Dragger
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        name={name}
        multiple={false}
        beforeUpload={(file: any, fileList: any[]) => handleFileChange(file)}
        {...props}
      >
        <p className="ant-upload-drag-icon">
          <Icon
            component={() => (
              <svg
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                p-id="2009"
              >
                <path
                  d="M557.248 507.968l113.152 113.152a32 32 0 0 1-42.24 47.904l-3.008-2.656L544 585.216V864a32 32 0 0 1-64 0v-278.784l-81.152 81.152a32 32 0 0 1-47.904-42.24l2.656-3.008 113.152-113.152a64 64 0 0 1 90.496 0zM512 160c131.2 0 239.392 98.752 254.272 225.984A224 224 0 0 1 736 832l-3.744-0.224a32 32 0 0 1 0-63.552L736 768a160 160 0 0 0 160-160 160.128 160.128 0 0 0-138.336-158.592l-49.216-6.656-5.76-49.344a192.032 192.032 0 0 0-381.344 0l-5.76 49.408-49.28 6.624a160.032 160.032 0 0 0 14.752 318.4L288 768a32 32 0 0 1 3.744 63.776L288 832a224 224 0 0 1-30.24-445.984A256 256 0 0 1 512 160z"
                  p-id="2010"
                ></path>
              </svg>
            )}
          />
        </p>
        <p className="ant-upload-text">{text}</p>
        <p className="ant-upload-hint">{hint}</p>
      </Dragger>
    </div>
  );
};

export default CSVFileUpload;
